import { useCallback, useEffect, useRef, useState } from "react";

function formatTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function formatBytes(n) {
  const num = Number(n) || 0;
  if (num < 1024) return `${num} B`;
  if (num < 1024 * 1024) return `${(num / 1024).toFixed(1)} KB`;
  return `${(num / 1024 / 1024).toFixed(1)} MB`;
}

const FILE_ACCEPT = "application/pdf,.pdf,image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif,video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov";
const MAX_BYTES = 150 * 1024 * 1024;

function kindBadge(kind) {
  switch (kind) {
    case "pdf":
      return "PDF";
    case "image":
      return "Image";
    case "video":
      return "Video";
    case "link":
      return "Link";
    default:
      return "File";
  }
}

export default function AdminLibraryPanel({ adminAxios }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [busyId, setBusyId] = useState("");

  // Inline edit state — which row is currently in edit mode + drafts
  // for the title/description fields. Only ONE row can be edited at a
  // time (matches the user's mental model: pick a row, edit it, save
  // or cancel before opening another).
  const [editingId, setEditingId] = useState("");
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const openEdit = (it) => {
    setEditingId(it.id);
    setEditTitle(it.title || "");
    setEditDescription(it.description || "");
    setInfo("");
    setError("");
  };

  const cancelEdit = () => {
    setEditingId("");
    setEditTitle("");
    setEditDescription("");
  };

  const saveEdit = async () => {
    if (!adminAxios || !editingId) return;
    setSavingEdit(true);
    setError("");
    setInfo("");
    try {
      await adminAxios.patch(`/library/${editingId}`, {
        title: editTitle,
        description: editDescription,
      });
      setInfo("Saved.");
      cancelEdit();
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Save failed.");
    } finally {
      setSavingEdit(false);
    }
  };

  const [mode, setMode] = useState("file"); // "file" | "link" | "pair"

  // File-upload form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Link form state
  const [linkTitle, setLinkTitle] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [linkDescription, setLinkDescription] = useState("");
  const [savingLink, setSavingLink] = useState(false);

  // Pair (video + study PDF) upload form state
  const [pairTitle, setPairTitle] = useState("");
  const [pairDescription, setPairDescription] = useState("");
  const [pairVideo, setPairVideo] = useState(null);
  const [pairStudy, setPairStudy] = useState(null);
  const [uploadingPair, setUploadingPair] = useState(false);

  // Pair-existing inline form state (which video row currently has the
  // "Pair with study" form open + the selected study id).
  const [pairingForVideoId, setPairingForVideoId] = useState("");
  const [pairingSelectedStudyId, setPairingSelectedStudyId] = useState("");
  const [savingPair, setSavingPair] = useState(false);

  const load = useCallback(async () => {
    if (!adminAxios) return;
    setLoading(true);
    setError("");
    try {
      // Admin sees ALL items including paired-child PDFs so they can
      // manage both sides of every pair.
      const res = await adminAxios.get("/library?include_paired_children=true");
      setItems(res.data?.items || []);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not load library.");
    } finally {
      setLoading(false);
    }
  }, [adminAxios]);

  useEffect(() => {
    load();
  }, [load]);

  const onUpload = async (e) => {
    e.preventDefault();
    if (!adminAxios) return;
    setError("");
    setInfo("");
    if (!file) {
      setError("Pick a file first.");
      return;
    }
    if (!title.trim()) {
      setError("Title is required.");
      return;
    }
    if (file.size > MAX_BYTES) {
      setError("File is larger than 150 MB.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title.trim());
      fd.append("description", description.trim());
      await adminAxios.post("/library/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setInfo("Uploaded.");
      setTitle("");
      setDescription("");
      setFile(null);
      const input = document.getElementById("library-file-input");
      if (input) input.value = "";
      await load();
    } catch (err) {
      // Surface as much detail as we can — production deployments
      // sometimes return a 413 / 502 from an upstream proxy with an
      // HTML body, in which case `err.response.data.detail` is empty.
      // Fall through to status code + raw body snippet so the admin
      // can diagnose without needing browser devtools.
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      let raw = err?.response?.data;
      if (raw && typeof raw !== "string") {
        try { raw = JSON.stringify(raw).slice(0, 200); } catch { raw = ""; }
      }
      let msg = "Upload failed.";
      if (typeof detail === "string" && detail) {
        msg = detail;
      } else if (status === 413) {
        msg = "Upload failed (413 — file blocked by the server proxy before it reached the app). Try a smaller file or contact support to raise the upload cap.";
      } else if (status === 502 || status === 504) {
        msg = `Upload failed (${status} — the server timed out or the upstream connection was reset). Try again or use a smaller file.`;
      } else if (status) {
        msg = `Upload failed (HTTP ${status})${raw ? `: ${String(raw).slice(0, 160)}` : ""}.`;
      } else if (err?.message) {
        msg = `Upload failed: ${err.message}`;
      }
      setError(msg);
    } finally {
      setUploading(false);
    }
  };

  const onAddLink = async (e) => {
    e.preventDefault();
    if (!adminAxios) return;
    setError("");
    setInfo("");
    if (!linkTitle.trim()) {
      setError("Title is required.");
      return;
    }
    const url = linkUrl.trim();
    if (!/^https?:\/\//i.test(url)) {
      setError("URL must start with http:// or https://");
      return;
    }
    setSavingLink(true);
    try {
      await adminAxios.post("/library/link", {
        title: linkTitle.trim(),
        url,
        description: linkDescription.trim(),
      });
      setInfo("Link added.");
      setLinkTitle("");
      setLinkUrl("");
      setLinkDescription("");
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Save failed.");
    } finally {
      setSavingLink(false);
    }
  };

  const onUploadPair = async (e) => {
    e.preventDefault();
    if (!adminAxios) return;
    setError("");
    setInfo("");
    if (!pairTitle.trim()) {
      setError("Title is required.");
      return;
    }
    if (!pairVideo) {
      setError("Pick a video file.");
      return;
    }
    if (!pairStudy) {
      setError("Pick a study PDF.");
      return;
    }
    if (pairVideo.size > MAX_BYTES || pairStudy.size > MAX_BYTES) {
      setError("Each file must be 150 MB or smaller.");
      return;
    }
    setUploadingPair(true);
    try {
      const fd = new FormData();
      fd.append("video", pairVideo);
      fd.append("study", pairStudy);
      fd.append("title", pairTitle.trim());
      fd.append("description", pairDescription.trim());
      await adminAxios.post("/library/upload-pair", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setInfo("Paired video + study uploaded.");
      setPairTitle("");
      setPairDescription("");
      setPairVideo(null);
      setPairStudy(null);
      const v = document.getElementById("library-pair-video-input");
      const s = document.getElementById("library-pair-study-input");
      if (v) v.value = "";
      if (s) s.value = "";
      await load();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      let msg = "Upload failed.";
      if (typeof detail === "string" && detail) msg = detail;
      else if (status === 413) msg = "One of the files exceeds the server cap.";
      else if (status) msg = `Upload failed (HTTP ${status}).`;
      setError(msg);
    } finally {
      setUploadingPair(false);
    }
  };

  const openPairForm = (videoId) => {
    setPairingForVideoId(videoId);
    setPairingSelectedStudyId("");
    setError("");
    setInfo("");
  };

  const cancelPairForm = () => {
    setPairingForVideoId("");
    setPairingSelectedStudyId("");
  };

  const submitPairExisting = async (videoId) => {
    if (!adminAxios) return;
    if (!pairingSelectedStudyId) {
      setError("Pick a study PDF to pair.");
      return;
    }
    setSavingPair(true);
    setError("");
    setInfo("");
    try {
      await adminAxios.patch(`/library/${videoId}/pair`, {
        study_id: pairingSelectedStudyId,
      });
      setInfo("Paired.");
      cancelPairForm();
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Pair failed.");
    } finally {
      setSavingPair(false);
    }
  };

  const onUnpair = async (videoId) => {
    if (!adminAxios) return;
    if (!window.confirm("Unpair this video from its study? The study will reappear as a standalone item.")) return;
    setSavingPair(true);
    setError("");
    setInfo("");
    try {
      await adminAxios.delete(`/library/${videoId}/pair`);
      setInfo("Unpaired.");
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Unpair failed.");
    } finally {
      setSavingPair(false);
    }
  };

  const onDelete = async (id) => {
    if (!adminAxios) return;
    if (!window.confirm("Delete this item permanently?")) return;
    setBusyId(id);
    setError("");
    setInfo("");
    try {
      await adminAxios.delete(`/library/${id}`);
      setInfo("Deleted.");
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Delete failed.");
    } finally {
      setBusyId("");
    }
  };

  // ── Drag-and-drop reorder ────────────────────────────────────
  // The admin can drag any row up or down to reshuffle the public
  // Library display sequence. We keep the change client-side until
  // the admin clicks "Save order" — gives them a clear undo path
  // (just navigate away or click "Reset") before committing.
  const dragSourceId = useRef(null);
  const [dragOverId, setDragOverId] = useState("");
  const [pendingOrder, setPendingOrder] = useState(null); // null = no pending change
  const [savingOrder, setSavingOrder] = useState(false);

  const onDragStart = (id) => (e) => {
    dragSourceId.current = id;
    try {
      e.dataTransfer.effectAllowed = "move";
      // Setting data is required in Firefox for the drag to register.
      e.dataTransfer.setData("text/plain", id);
    } catch {
      /* ignore — older browsers */
    }
  };

  const onDragOver = (id) => (e) => {
    e.preventDefault();
    try {
      e.dataTransfer.dropEffect = "move";
    } catch {
      /* ignore */
    }
    if (dragOverId !== id) setDragOverId(id);
  };

  const onDragEnd = () => {
    dragSourceId.current = null;
    setDragOverId("");
  };

  const onDrop = (targetId) => (e) => {
    e.preventDefault();
    const sourceId = dragSourceId.current;
    dragSourceId.current = null;
    setDragOverId("");
    if (!sourceId || sourceId === targetId) return;
    const current = pendingOrder || items.map((it) => it.id);
    const fromIdx = current.indexOf(sourceId);
    const toIdx = current.indexOf(targetId);
    if (fromIdx < 0 || toIdx < 0) return;
    const next = [...current];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    setPendingOrder(next);
  };

  const onResetOrder = () => {
    setPendingOrder(null);
    setInfo("");
    setError("");
  };

  const onSaveOrder = async () => {
    if (!adminAxios || !pendingOrder) return;
    setSavingOrder(true);
    setError("");
    setInfo("");
    try {
      await adminAxios.post("/library/reorder", { order: pendingOrder });
      setInfo("Order saved.");
      setPendingOrder(null);
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Save order failed.");
    } finally {
      setSavingOrder(false);
    }
  };

  // Derive the items in their current display order — either the
  // pending (unsaved) order or the server order.
  const displayItems = (() => {
    if (!pendingOrder) return items;
    const byId = new Map(items.map((it) => [it.id, it]));
    const reordered = [];
    for (const id of pendingOrder) {
      const it = byId.get(id);
      if (it) reordered.push(it);
    }
    // Append any items that exist on the server but aren't in the
    // pending order (defensive — should not happen in normal flow).
    for (const it of items) {
      if (!pendingOrder.includes(it.id)) reordered.push(it);
    }
    return reordered;
  })();

  // ── Pair-management derived data ─────────────────────────────
  // - `pdfItems` is every PDF the admin can pick as a study.
  // - `pairedStudyIdToVideo` maps a paired PDF's id back to the
  //   video that claims it, so the row UI can show "Paired with…"
  //   on the PDF side and offer Unpair on the video side.
  const pdfItems = items.filter((it) => it.kind === "pdf");
  const pairedStudyIdToVideo = new Map();
  for (const it of items) {
    if (it.kind === "video" && it.paired_id) {
      pairedStudyIdToVideo.set(it.paired_id, it);
    }
  }

  return (
    <div data-testid="admin-library-panel">
      {/* Mode toggle */}
      <div className="rm-admin-tabs" data-testid="library-mode-tabs">
        <button
          type="button"
          className={`rm-admin-tab ${mode === "file" ? "is-active" : ""}`}
          onClick={() => {
            setMode("file");
            setError("");
            setInfo("");
          }}
          data-testid="library-mode-file"
        >
          Upload file
        </button>
        <button
          type="button"
          className={`rm-admin-tab ${mode === "pair" ? "is-active" : ""}`}
          onClick={() => {
            setMode("pair");
            setError("");
            setInfo("");
          }}
          data-testid="library-mode-pair"
        >
          Upload video + study
        </button>
        <button
          type="button"
          className={`rm-admin-tab ${mode === "link" ? "is-active" : ""}`}
          onClick={() => {
            setMode("link");
            setError("");
            setInfo("");
          }}
          data-testid="library-mode-link"
        >
          Add link
        </button>
      </div>

      {mode === "file" && (
        <form
          className="rm-admin-library-form"
          onSubmit={onUpload}
          data-testid="library-upload-form"
        >
          <h3
            className="font-serif-display italic text-2xl text-[#FFE7A0] mb-4"
            data-testid="library-upload-title"
          >
            Upload a file
          </h3>
          <p className="rm-admin-library-helper">
            PDF · JPG / PNG / WEBP / GIF · MP4 / WEBM / MOV &middot; max 150 MB
          </p>
          <label className="rm-admin-library-field">
            <span className="rm-admin-library-label">Title</span>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              placeholder="e.g. The Restoration Matrix Brain (PDF)"
              className="rm-admin-library-input"
              data-testid="library-upload-title-input"
              required
            />
          </label>
          <label className="rm-admin-library-field">
            <span className="rm-admin-library-label">Short description</span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={600}
              placeholder="One or two lines on what this file is."
              className="rm-admin-library-input rm-admin-library-textarea"
              rows={2}
              data-testid="library-upload-description-input"
            />
          </label>
          <label className="rm-admin-library-field">
            <span className="rm-admin-library-label">File (max 150 MB)</span>
            <input
              id="library-file-input"
              type="file"
              accept={FILE_ACCEPT}
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="rm-admin-library-file"
              data-testid="library-upload-file-input"
              required
            />
          </label>
          <div className="rm-admin-library-form-actions">
            <button
              type="submit"
              className="rm-btn-solid"
              disabled={uploading}
              data-testid="library-upload-submit"
            >
              {uploading ? "Uploading…" : "Upload"}
            </button>
          </div>
        </form>
      )}

      {mode === "pair" && (
        <form
          className="rm-admin-library-form"
          onSubmit={onUploadPair}
          data-testid="library-pair-upload-form"
        >
          <h3
            className="font-serif-display italic text-2xl text-[#FFE7A0] mb-4"
            data-testid="library-pair-upload-title"
          >
            Upload a video and its study
          </h3>
          <p className="rm-admin-library-helper">
            Both files upload at once. The study PDF will be hidden from the public list and appear only inside the paired video block. Max 150 MB per file.
          </p>
          <label className="rm-admin-library-field">
            <span className="rm-admin-library-label">Block title (shown on the video card)</span>
            <input
              type="text"
              value={pairTitle}
              onChange={(e) => setPairTitle(e.target.value)}
              maxLength={200}
              placeholder="e.g. Jacob's Ladder — Severance Teaching"
              className="rm-admin-library-input"
              data-testid="library-pair-title-input"
              required
            />
          </label>
          <label className="rm-admin-library-field">
            <span className="rm-admin-library-label">Short description</span>
            <textarea
              value={pairDescription}
              onChange={(e) => setPairDescription(e.target.value)}
              maxLength={600}
              placeholder="One or two lines describing the video + study together."
              className="rm-admin-library-input rm-admin-library-textarea"
              rows={2}
              data-testid="library-pair-description-input"
            />
          </label>
          <label className="rm-admin-library-field">
            <span className="rm-admin-library-label">Video file (.mp4 / .webm / .mov)</span>
            <input
              id="library-pair-video-input"
              type="file"
              accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov"
              onChange={(e) => setPairVideo(e.target.files?.[0] || null)}
              className="rm-admin-library-file"
              data-testid="library-pair-video-input"
              required
            />
          </label>
          <label className="rm-admin-library-field">
            <span className="rm-admin-library-label">Study PDF</span>
            <input
              id="library-pair-study-input"
              type="file"
              accept="application/pdf,.pdf"
              onChange={(e) => setPairStudy(e.target.files?.[0] || null)}
              className="rm-admin-library-file"
              data-testid="library-pair-study-input"
              required
            />
          </label>
          <div className="rm-admin-library-form-actions">
            <button
              type="submit"
              className="rm-btn-solid"
              disabled={uploadingPair}
              data-testid="library-pair-submit"
            >
              {uploadingPair ? "Uploading both…" : "Upload pair"}
            </button>
          </div>
        </form>
      )}

      {mode === "link" && (
        <form
          className="rm-admin-library-form"
          onSubmit={onAddLink}
          data-testid="library-link-form"
        >
          <h3
            className="font-serif-display italic text-2xl text-[#FFE7A0] mb-4"
            data-testid="library-link-title"
          >
            Add an external link
          </h3>
          <p className="rm-admin-library-helper">
            YouTube, articles, Bandcamp, etc. &middot; opens in a new tab
          </p>
          <label className="rm-admin-library-field">
            <span className="rm-admin-library-label">Title</span>
            <input
              type="text"
              value={linkTitle}
              onChange={(e) => setLinkTitle(e.target.value)}
              maxLength={200}
              placeholder="e.g. Worship set — Restoration Matrix Vol. 1"
              className="rm-admin-library-input"
              data-testid="library-link-title-input"
              required
            />
          </label>
          <label className="rm-admin-library-field">
            <span className="rm-admin-library-label">URL (https://…)</span>
            <input
              type="url"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              maxLength={2000}
              placeholder="https://www.youtube.com/watch?v=…"
              className="rm-admin-library-input"
              data-testid="library-link-url-input"
              required
            />
          </label>
          <label className="rm-admin-library-field">
            <span className="rm-admin-library-label">Short description</span>
            <textarea
              value={linkDescription}
              onChange={(e) => setLinkDescription(e.target.value)}
              maxLength={600}
              placeholder="One or two lines on what this link is."
              className="rm-admin-library-input rm-admin-library-textarea"
              rows={2}
              data-testid="library-link-description-input"
            />
          </label>
          <div className="rm-admin-library-form-actions">
            <button
              type="submit"
              className="rm-btn-solid"
              disabled={savingLink}
              data-testid="library-link-submit"
            >
              {savingLink ? "Saving…" : "Add link"}
            </button>
          </div>
        </form>
      )}

      {error && (
        <div className="rm-cm-alert rm-cm-alert-error mt-6" data-testid="library-error">
          {error}
        </div>
      )}
      {info && (
        <div className="rm-cm-alert rm-cm-alert-info mt-6" data-testid="library-info">
          {info}
        </div>
      )}

      <div className="mt-10" data-testid="admin-library-list">
        <h3
          className="font-serif-display italic text-2xl text-[#FFE7A0] mb-4"
          data-testid="library-list-title"
        >
          Current library
        </h3>
        {loading && (
          <div className="rm-cm-empty" data-testid="library-loading">
            Loading…
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="rm-cm-empty" data-testid="library-empty">
            No items uploaded yet.
          </div>
        )}
        {!loading && items.length > 0 && (
          <>
            {pendingOrder && (
              <div
                className="rm-admin-reorder-bar"
                data-testid="library-reorder-bar"
                style={{
                  display: "flex",
                  gap: "0.75rem",
                  alignItems: "center",
                  padding: "0.75rem 1rem",
                  marginBottom: "0.75rem",
                  background: "rgba(184, 134, 11, 0.12)",
                  border: "1px solid rgba(184, 134, 11, 0.4)",
                  borderRadius: "0.5rem",
                }}
              >
                <span style={{ flex: 1, fontSize: "0.9rem" }}>
                  Order changed — preview shown below. Save to apply.
                </span>
                <button
                  type="button"
                  className="rm-cm-link rm-cm-link-muted"
                  onClick={onResetOrder}
                  disabled={savingOrder}
                  data-testid="library-reorder-reset"
                >
                  Reset
                </button>
                <button
                  type="button"
                  className="rm-cm-link"
                  onClick={onSaveOrder}
                  disabled={savingOrder}
                  data-testid="library-reorder-save"
                >
                  {savingOrder ? "Saving…" : "Save order"}
                </button>
              </div>
            )}
            <p
              className="rm-admin-hint"
              style={{ fontSize: "0.8rem", opacity: 0.7, marginBottom: "0.5rem" }}
            >
              Drag a row to reshuffle the public Library display order.
            </p>
            <ul className="rm-admin-rows" data-testid="library-rows">
              {displayItems.map((it) => (
                <li
                  key={it.id}
                  className={`rm-admin-row${dragOverId === it.id ? " rm-admin-row-dragover" : ""}`}
                  onDragOver={onDragOver(it.id)}
                  onDragLeave={() => dragOverId === it.id && setDragOverId("")}
                  onDrop={onDrop(it.id)}
                  data-testid={`library-row-${it.id}`}
                  style={{
                    display: "flex",
                    alignItems: "stretch",
                    gap: "0.75rem",
                    ...(dragOverId === it.id
                      ? {
                          outline: "2px dashed rgba(184, 134, 11, 0.85)",
                          outlineOffset: "-2px",
                        }
                      : {}),
                  }}
                >
                  {/* Explicit drag handle — the ONLY element on the
                      row that initiates a drag. Keeps text selection,
                      "Open" links, and "Delete" buttons usable
                      without hijacking. */}
                  <div
                    draggable
                    onDragStart={onDragStart(it.id)}
                    onDragEnd={onDragEnd}
                    aria-label={`Drag to reorder ${it.title || "library item"}`}
                    title="Drag to reorder"
                    data-testid={`library-row-handle-${it.id}`}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "28px",
                      flexShrink: 0,
                      cursor: "grab",
                      color: "rgba(255, 231, 160, 0.55)",
                      fontSize: "1.25rem",
                      lineHeight: 1,
                      userSelect: "none",
                      WebkitUserSelect: "none",
                      touchAction: "none",
                    }}
                    onMouseDown={(e) => {
                      e.currentTarget.style.cursor = "grabbing";
                    }}
                    onMouseUp={(e) => {
                      e.currentTarget.style.cursor = "grab";
                    }}
                  >
                    ⋮⋮
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                {editingId === it.id ? (
                  <div
                    className="rm-admin-row-edit"
                    data-testid={`library-row-edit-${it.id}`}
                    style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}
                  >
                    <input
                      type="text"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      placeholder="Title"
                      className="rm-form-input"
                      data-testid={`library-row-edit-title-${it.id}`}
                      style={{ width: "100%" }}
                    />
                    <textarea
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      placeholder="Description (optional)"
                      className="rm-form-textarea"
                      rows={3}
                      data-testid={`library-row-edit-description-${it.id}`}
                      style={{ width: "100%", resize: "vertical" }}
                    />
                    <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                      <button
                        type="button"
                        className="rm-cm-link"
                        disabled={savingEdit}
                        onClick={saveEdit}
                        data-testid={`library-row-edit-save-${it.id}`}
                      >
                        {savingEdit ? "Saving…" : "Save"}
                      </button>
                      <button
                        type="button"
                        className="rm-cm-link rm-cm-link-muted"
                        disabled={savingEdit}
                        onClick={cancelEdit}
                        data-testid={`library-row-edit-cancel-${it.id}`}
                      >
                        Cancel
                      </button>
                      <span style={{ marginLeft: "auto", fontSize: "0.75rem", opacity: 0.6 }}>
                        {kindBadge(it.kind)}
                        {it.kind !== "link" && it.size_bytes ? ` · ${formatBytes(it.size_bytes)}` : ""}
                      </span>
                    </div>
                  </div>
                ) : (
                  <>
                <div className="rm-admin-row-meta">
                  <span className="rm-cm-name">{it.title}</span>
                  <span className="rm-cm-time">{formatTime(it.uploaded_at)}</span>
                  <span className="rm-admin-badge">
                    {kindBadge(it.kind)}
                    {it.kind !== "link" && it.size_bytes ? ` · ${formatBytes(it.size_bytes)}` : ""}
                  </span>
                  {it.kind === "video" && it.paired_id && (
                    <span
                      className="rm-admin-badge"
                      data-testid={`library-row-paired-badge-${it.id}`}
                      title="This video has a paired study PDF"
                      style={{ background: "rgba(184, 134, 11, 0.22)", color: "#FFE7A0" }}
                    >
                      ⛓ Paired
                    </span>
                  )}
                  {it.kind === "pdf" && pairedStudyIdToVideo.has(it.id) && (
                    <span
                      className="rm-admin-badge"
                      data-testid={`library-row-hidden-badge-${it.id}`}
                      title="Hidden from public list — appears inside the paired video block"
                      style={{ background: "rgba(184, 134, 11, 0.18)", color: "#FFE7A0" }}
                    >
                      ↳ Study of "{pairedStudyIdToVideo.get(it.id)?.title || "video"}"
                    </span>
                  )}
                  <span
                    className="rm-admin-badge rm-admin-stat-badge"
                    data-testid={`library-row-stats-${it.id}`}
                    title="Total downloads · views"
                  >
                    ↓ {Number(it.download_count || 0)}
                    {" · "}
                    👁 {Number(it.view_count || 0)}
                  </span>
                </div>
                {it.description && (
                  <p className="rm-admin-row-body">{it.description}</p>
                )}
                {it.kind === "video" && pairingForVideoId === it.id && (
                  <div
                    className="rm-admin-pair-form"
                    data-testid={`library-row-pair-form-${it.id}`}
                    style={{
                      display: "flex",
                      gap: "0.5rem",
                      alignItems: "center",
                      flexWrap: "wrap",
                      margin: "0.5rem 0",
                      padding: "0.5rem 0.75rem",
                      background: "rgba(184, 134, 11, 0.08)",
                      border: "1px solid rgba(184, 134, 11, 0.3)",
                      borderRadius: "0.5rem",
                    }}
                  >
                    <span style={{ fontSize: "0.85rem", opacity: 0.85 }}>
                      Pair with study:
                    </span>
                    <select
                      className="rm-form-input"
                      value={pairingSelectedStudyId}
                      onChange={(e) => setPairingSelectedStudyId(e.target.value)}
                      data-testid={`library-row-pair-select-${it.id}`}
                      style={{ flex: 1, minWidth: "12rem" }}
                    >
                      <option value="">— pick a PDF —</option>
                      {pdfItems
                        .filter(
                          (pdf) =>
                            !pairedStudyIdToVideo.has(pdf.id) ||
                            pairedStudyIdToVideo.get(pdf.id)?.id === it.id,
                        )
                        .map((pdf) => (
                          <option key={pdf.id} value={pdf.id}>
                            {pdf.title}
                          </option>
                        ))}
                    </select>
                    <button
                      type="button"
                      className="rm-cm-link"
                      disabled={savingPair || !pairingSelectedStudyId}
                      onClick={() => submitPairExisting(it.id)}
                      data-testid={`library-row-pair-confirm-${it.id}`}
                    >
                      {savingPair ? "Pairing…" : "Pair"}
                    </button>
                    <button
                      type="button"
                      className="rm-cm-link rm-cm-link-muted"
                      disabled={savingPair}
                      onClick={cancelPairForm}
                      data-testid={`library-row-pair-cancel-${it.id}`}
                    >
                      Cancel
                    </button>
                  </div>
                )}
                <div className="rm-admin-row-actions">
                  {it.kind === "link" ? (
                    <a
                      className="rm-cm-link"
                      href={it.url}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`library-row-open-${it.id}`}
                    >
                      Open
                    </a>
                  ) : (
                    <a
                      className="rm-cm-link"
                      href={`/api/library/${it.id}/file`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`library-row-view-${it.id}`}
                    >
                      View
                    </a>
                  )}
                  <button
                    type="button"
                    className="rm-cm-link"
                    disabled={busyId === it.id}
                    onClick={() => openEdit(it)}
                    data-testid={`library-row-edit-open-${it.id}`}
                  >
                    Edit
                  </button>
                  {it.kind === "video" && !it.paired_id && pairingForVideoId !== it.id && pdfItems.length > 0 && (
                    <button
                      type="button"
                      className="rm-cm-link"
                      disabled={savingPair}
                      onClick={() => openPairForm(it.id)}
                      data-testid={`library-row-pair-open-${it.id}`}
                    >
                      Pair with study…
                    </button>
                  )}
                  {it.kind === "video" && it.paired_id && (
                    <button
                      type="button"
                      className="rm-cm-link rm-cm-link-muted"
                      disabled={savingPair}
                      onClick={() => onUnpair(it.id)}
                      data-testid={`library-row-unpair-${it.id}`}
                    >
                      Unpair study
                    </button>
                  )}
                  <button
                    type="button"
                    className="rm-cm-link rm-cm-link-muted rm-admin-danger"
                    disabled={busyId === it.id}
                    onClick={() => onDelete(it.id)}
                    data-testid={`library-row-delete-${it.id}`}
                  >
                    Delete
                  </button>
                </div>
                  </>
                )}
                  </div>
              </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
